import { useEffect, useState } from 'react';
import { VariationDisplay } from '@gonfalon/flag-variations/VariationDisplay';
import type { Variation } from '@gonfalon/flags/types';
import { Message } from '@gonfalon/messages/Message';
import { msg } from '@gonfalon/messages/msg';
import { Focusable, ProgressBar, Tooltip, TooltipTrigger } from '@launchpad-ui/components';
import { Icon } from '@launchpad-ui/icons';
import cx from 'clsx';

import { ImagePreviewModal } from '../ImagePreviewModal';

import styles from './imageThumbnail.module.css';

type ImageThumbnailProps = {
  /** Whether the image thumbnail should be clickable and open a preview modal */
  allowPreviewClick?: boolean;
  enableTooltip?: boolean;
  variation?: Variation;
  variationIndex?: number;
  src?: string | null;
  alt?: string;
  size?: 'small' | 'medium' | 'large' | 'full';
  className?: string;
  onClick?: () => void;
  onImageLoad?: (naturalWidth: number, naturalHeight: number) => void;
  /** Show loading indicator instead of image/placeholder */
  isLoading?: boolean;
};

export const ImageThumbnail: React.FC<ImageThumbnailProps> = ({
  allowPreviewClick = true,
  enableTooltip = false,
  variation,
  variationIndex,
  src,
  alt = msg('experiments.variationImageThumbnail.defaultAlt'),
  size = 'full',
  className,
  onClick,
  onImageLoad,
  isLoading = false,
}) => {
  const [imageResolution, setImageResolution] = useState<{ naturalWidth: number; naturalHeight: number } | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  // Reset image resolution when src changes
  useEffect(() => {
    setImageResolution(null);
  }, [src]);

  const isLandscape =
    imageResolution?.naturalWidth &&
    imageResolution?.naturalHeight &&
    imageResolution.naturalWidth >= 2 * imageResolution.naturalHeight;
  const isPortrait =
    imageResolution?.naturalWidth &&
    imageResolution?.naturalHeight &&
    imageResolution.naturalHeight >= 2 * imageResolution.naturalWidth;

  const hasPreviewClick = allowPreviewClick && src;

  const handleImageLoad = (event: React.SyntheticEvent<HTMLImageElement>) => {
    const img = event.currentTarget;
    const { naturalWidth, naturalHeight } = img;
    setImageResolution({ naturalWidth, naturalHeight });

    if (onImageLoad) {
      onImageLoad(naturalWidth, naturalHeight);
    }
  };

  const handleClick = () => {
    if (onClick) {
      onClick();
      return;
    }

    if (hasPreviewClick) {
      setIsModalOpen(true);
    }
  };

  return (
    <TooltipTrigger>
      <Focusable>
        <button
          type="button"
          className={cx(
            styles.imageContainer,
            size && styles[size],
            (hasPreviewClick || onClick) && styles.withPointer,
            className,
          )}
          onClick={handleClick}
        >
          {isLoading ? (
            <div className={styles.loadingContainer}>
              <ProgressBar
                aria-label={msg('experiments.variationImageThumbnail.loadingLabel')}
                isIndeterminate
                size="small"
              />
            </div>
          ) : src ? (
            <img
              className={cx(
                styles.imageThumbnail,
                isLandscape && styles.withLandscapeOrientation,
                isPortrait && styles.withPortraitOrientation,
              )}
              src={src}
              alt={alt}
              onLoad={handleImageLoad}
            />
          ) : (
            <div className={styles.placeholderContainer}>
              <Icon name="photo" variant="subtle" height="100%" width="100%" />
            </div>
          )}
          <ImagePreviewModal
            isOpen={isModalOpen}
            variation={variation}
            variationIndex={variationIndex}
            src={src ?? undefined}
            onOpenChange={setIsModalOpen}
          />
        </button>
      </Focusable>
      <Tooltip variant={null} placement="top start" isOpen={enableTooltip && src ? undefined : false}>
        <div className={styles.tooltipContainer}>
          {variation && typeof variationIndex === 'number' ? (
            <VariationDisplay
              className={styles.variationDisplay}
              variation={variation}
              variationIndex={variationIndex}
            />
          ) : (
            <span className={styles.variationDisplay}>
              <Message id="experiments.variationImageThumbnail.previewLabel" />
            </span>
          )}
          {src && (
            <div className={styles.imageContainer}>
              <img
                className={cx(
                  styles.imageThumbnail,
                  isLandscape && styles.withLandscapeOrientation,
                  isPortrait && styles.withPortraitOrientation,
                )}
                src={src}
                alt={alt}
              />
            </div>
          )}
        </div>
      </Tooltip>
    </TooltipTrigger>
  );
};
